// Charts/page.js — Burndown, Planned vs Actual, Velocity Trend
// FIX: All sprints now shown (not just last 5/6). Scrolls horizontally when many sprints.
import React, { useMemo, useEffect, useRef } from "react";
import { Row, Col, Card } from "react-bootstrap";
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis,
  CartesianGrid, Tooltip, ResponsiveContainer, Legend,
  ReferenceLine, Cell
} from "recharts";

const sid = (v) => String(v ?? "");

const getStoriesForSprint = (allStories, sprintId) => {
  const spId = sid(sprintId);
  return allStories.filter((st) => {
    if (st.sprint?.id !== undefined) return sid(st.sprint.id) === spId;
    if (st.sprintId   !== undefined) return sid(st.sprintId)  === spId;
    return false;
  });
};

const ChartCard = ({ title, subtitle, children }) => (
  <Card style={{
    borderRadius: 12, border: "1px solid #e5e7eb",
    boxShadow: "0 1px 4px rgba(0,0,0,.06)", height: "100%", overflow: "hidden"
  }}>
    <div style={{ padding: "14px 18px 2px" }}>
      <div style={{ fontSize: 13, fontWeight: 600, color: "#111827" }}>{title}</div>
      {subtitle && <div style={{ fontSize: 11, color: "#9ca3af" }}>{subtitle}</div>}
    </div>
    <div style={{ padding: "8px 12px 14px" }}>{children}</div>
  </Card>
);

// ─── How many px wide each bar/tick should be ───────────────────────────────
// Gives each sprint enough room so labels never overlap
const PX_PER_SPRINT = 64;   // for bar charts (Planned vs Actual)
const PX_PER_VEL    = 56;   // for velocity line chart
const MIN_WIDTH     = 300;  // never smaller than this
// ADD THIS after ChartCard definition
const sp = (val) => {
  if (val === null || val === undefined || val === "") return 0;
  const n = Number(val);
  return isNaN(n) ? 0 : n;
};

// Normalize any status variation to standard values
const normalizeStatus = (status) => {
  if (!status) return "TODO";
  const s = status.trim().toLowerCase().replace(/\s+/g, "");
  if (s === "completed")   return "COMPLETED";
  if (s === "inprogress")  return "INPROGRESS";
  if (s === "intesting")   return "INTESTING";
  if (s === "todo")        return "TODO";
  return "TODO"; // default unknown to TODO
};

const Charts = ({ sprint, stories, allSprints, allStories }) => {
  const pvRef  = useRef(null);
  const velRef = useRef(null);

  const sortedSprints = useMemo(() => {
    return [...allSprints]
      .filter((s) => s.startDate && s.endDate && s.sprintName?.toLowerCase() !== "backlog")
      .sort((a, b) => new Date(a.startDate) - new Date(b.startDate));
  }, [allSprints]);

  // When selected sprint changes, scroll that sprint into center view
  useEffect(() => {
    if (!sprint || !sortedSprints.length) return;
    const idx = sortedSprints.findIndex((s) => sid(s.id) === sid(sprint.id));
    if (idx === -1) return;

    const scrollOne = (el, pxPer) => {
      if (!el) return;
      const containerWidth = el.clientWidth;
      if (containerWidth === 0) return; // not painted yet
      const targetCenter = idx * pxPer + pxPer / 2;
      const scrollLeft   = targetCenter - containerWidth / 2;
      el.scrollTo({ left: Math.max(0, scrollLeft), behavior: "smooth" });
    };

    const doScroll = () => {
      scrollOne(pvRef.current,  PX_PER_SPRINT);
      scrollOne(velRef.current, PX_PER_VEL);
    };

    // For each ref, watch until it has a real width, then scroll once
    const observers = [
      { ref: pvRef,  pxPer: PX_PER_SPRINT },
      { ref: velRef, pxPer: PX_PER_VEL    },
    ].map(({ ref, pxPer }) => {
      const ro = new ResizeObserver((entries) => {
        for (const entry of entries) {
          if (entry.contentRect.width > 0) {
            scrollOne(ref.current, pxPer);
            ro.disconnect(); // scroll once, then stop watching
          }
        }
      });
      if (ref.current) ro.observe(ref.current);
      return ro;
    });

    // Also try immediately + small delay for dropdown-change case
    doScroll();
    const t = setTimeout(doScroll, 150);

    return () => {
      observers.forEach((ro) => ro.disconnect());
      clearTimeout(t);
    };
  }, [sprint, sortedSprints]);
  // ── Burndown Chart ─────────────────────────────────────────────────────────
const burndownData = useMemo(() => {
    if (!sprint || !stories.length) return [];

    const start   = new Date(sprint.startDate);
    const end     = new Date(sprint.endDate);
    const today   = new Date(); today.setHours(0, 0, 0, 0);
    const days    = Math.max(1, Math.round((end - start) / 86400000));
const totalSP = stories.reduce((s, st) => s + sp(st.storyPoints) + sp(st.testStoryPoints), 0);
    // ── How many days have elapsed (capped at sprint length) ──
    const elapsedDays = Math.min(
      days,
      Math.max(0, Math.round((today - start) / 86400000))
    );

    // ── Current real values (single snapshot, no interpolation) ──
    // Green: SP of stories marked COMPLETED
    const completedSP = stories
      .filter((s) => normalizeStatus(s.status) === "COMPLETED")
  .reduce((s, st) => s + sp(st.storyPoints) + sp(st.testStoryPoints), 0);

    // Blue: SP of TODO + INPROGRESS + INTESTING (everything not done)
    const remainingSP = stories
      .filter((s) => normalizeStatus(s.status) !== "COMPLETED")
  .reduce((s, st) => s + sp(st.storyPoints) + sp(st.testStoryPoints), 0);

    const startEntry = { day: "D0", ideal: totalSP, completed: 0, remaining: totalSP };
    return [startEntry, ...Array.from({ length: days + 1 }, (_, dayIndex) => {
      // Grey dashed ideal: starts at totalSP, drops to 0 linearly
      const ideal = Math.round(totalSP - (totalSP / days) * dayIndex);

      // Future days — only show ideal line, no actual data
      if (dayIndex > elapsedDays) {
        return { day: `D${dayIndex + 1}`, ideal, completed: null, remaining: null };
      }

      // Day 0 (sprint start) — show 0 only if sprint just started and nothing elapsed yet
if (dayIndex === 0 && elapsedDays > 0) {
  return { day: `D${dayIndex + 1}`, ideal, completed: 0, remaining: totalSP };
}

      // Past days up to today — show current real snapshot at today's point
      // For all past days we show the same real value (flat until today)
      // because we don't have per-day history
      if (dayIndex < elapsedDays) {
        // Interpolate so the chart looks progressive rather than flat
        const ratio = dayIndex / elapsedDays;
        return {
          day:       `D${dayIndex + 1}`,
          ideal,
          completed: Math.round(completedSP * ratio),
          remaining: Math.round(totalSP - completedSP * ratio),
        };
      }

      // Today — show exact real values
      return {
        day:       `D${dayIndex + 1}`,
        ideal,
        completed: completedSP,
        remaining: remainingSP,
      };
   })];
  }, [sprint, stories]);

  // ── Planned vs Actual — ALL sprints, not just last 5 ──────────────────────
  const plannedVsActual = useMemo(() => {
    return sortedSprints.map((spItem, i) => {
      const spSt = getStoriesForSprint(allStories, spItem.id);

      // ✅ For COMPLETED sprints, prefer the backend snapshot captured at
      // stop-time (before incomplete stories were moved to Backlog/another
      // sprint) — otherwise "planned" and "actual" both shrink to only the
      // stories still linked to this sprint, wrongly making them equal.
      const hasSnapshot = spItem.sprintStatus === "COMPLETED" && spItem.snapshotTargetSP != null;

      return {
        name:    spItem.sprintName || `S${i + 1}`,
        planned: hasSnapshot
          ? (spItem.snapshotTargetSP || 0)
          : spSt.reduce((s, st) => s + sp(st.storyPoints) + sp(st.testStoryPoints), 0),
        actual: hasSnapshot
          ? (spItem.snapshotAchievedSP || 0)
          : spSt
              .filter((st) => normalizeStatus(st.status) === "COMPLETED")
              .reduce((s, st) => s + sp(st.storyPoints) + sp(st.testStoryPoints), 0),
      };
    });
  }, [sortedSprints, allStories]);

  // ── Velocity Trend — ALL sprints, not just last 6 ─────────────────────────
 const velocityData = useMemo(() => {
    return sortedSprints.map((spItem, i) => {
      const spSt = getStoriesForSprint(allStories, spItem.id);

      // ✅ Same fix as Planned vs Actual — completed sprints use the
      // backend snapshot's achieved SP instead of recalculating from
      // whatever stories remain linked (which would drop once incomplete
      // ones are moved out).
      const hasSnapshot = spItem.sprintStatus === "COMPLETED" && spItem.snapshotAchievedSP != null;

      return {
        name: spItem.sprintName || `S${i + 1}`,
        velocity: hasSnapshot
          ? (spItem.snapshotAchievedSP || 0)
          : spSt
              .filter((st) => normalizeStatus(st.status) === "COMPLETED")
              .reduce((s, st) => s + sp(st.storyPoints) + sp(st.testStoryPoints), 0),
      };
    });
  }, [sortedSprints, allStories]);

  // ── Dynamic widths so every sprint gets its own readable space ─────────────
const totalSprintCount    = sortedSprints.length;
  const pvActualInnerWidth  = Math.max(totalSprintCount * PX_PER_SPRINT, MIN_WIDTH);
  const velocityInnerWidth  = Math.max(totalSprintCount * PX_PER_VEL,    MIN_WIDTH);
  const burndownInnerWidth  = Math.max(burndownData.length * 28,          MIN_WIDTH);

  return (
    <Row className="g-3 mb-3">

      {/* ── Burndown Chart ── */}
      <Col md={4}>
        <ChartCard title="Burndown Chart" subtitle="Ideal vs Remaining effort">
          <div style={{ overflowX: "auto", overflowY: "hidden" }}>
            <div style={{ width: burndownInnerWidth, minWidth: "100%" }}>
              <LineChart
                width={burndownInnerWidth}
                height={160}
                data={burndownData}
                margin={{ top: 4, right: 8, left: -22, bottom: 0 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                <XAxis dataKey="day" tick={{ fontSize: 10, fill: "#9ca3af" }} />
                <YAxis tick={{ fontSize: 10, fill: "#9ca3af" }} />
                <Tooltip
                  contentStyle={{ fontSize: 11, borderRadius: 8 }}
                  formatter={(value, name) => [value ?? "—", name]}
                />
                <Legend iconSize={8} wrapperStyle={{ fontSize: 10 }} />
                <Line type="monotone" dataKey="completed" stroke="#16a34a" strokeWidth={2} dot={{ r: 3 }} name="Completed" connectNulls={false} />
<Line type="monotone" dataKey="ideal"     stroke="#9ca3af" strokeWidth={1.5} strokeDasharray="4 2" dot={false} name="Ideal" connectNulls={false} />
<Line type="monotone" dataKey="remaining" stroke="#2563eb" strokeWidth={2} dot={{ r: 3 }} name="Remaining" connectNulls={false} />
              </LineChart>
            </div>
          </div>
          {burndownData.length > 15 && (
            <div style={{ fontSize: 10, color: "#9ca3af", textAlign: "center", marginTop: 2 }}>
              ← scroll to see all {burndownData.length - 1} days →
            </div>
          )}
        </ChartCard>
      </Col>

      {/* ── Planned vs Actual — ALL sprints with scroll ── */}
      <Col md={4}>
        <ChartCard
          title="Planned vs Actual"
          subtitle={`Story points comparison · ${totalSprintCount} sprints`}
        >
          {/* 
            KEY FIX: overflowX:auto makes this scrollable.
            The inner div expands to fit ALL sprints (pvActualInnerWidth).
            User can scroll left ↔ right to see Sprint 1 through Sprint N.
          */}
          <div ref={pvRef} style={{ overflowX: "auto", overflowY: "hidden" }}>
            <div style={{ width: pvActualInnerWidth, minWidth: "100%" }}>
              <BarChart
                width={pvActualInnerWidth}
                height={totalSprintCount > 8 ? 195 : 175}
                data={plannedVsActual}
                barGap={2}
                barCategoryGap="30%"
                margin={{ top: 4, right: 8, left: -22, bottom: totalSprintCount > 8 ? 55 : 4 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                <Legend
                  verticalAlign="top"
                  iconSize={8}
                  wrapperStyle={{ fontSize: 10, paddingBottom: 4 }}
                />
                <XAxis
                  dataKey="name"
                  tick={{ fontSize: 10, fill: "#9ca3af" }}
                  interval={0}          // ← show EVERY sprint label, never skip
                  angle={totalSprintCount > 8 ? -35 : 0}   // tilt labels if crowded
                  textAnchor={totalSprintCount > 8 ? "end" : "middle"}
                  height={totalSprintCount > 8 ? 50 : 20}
                />
                <YAxis tick={{ fontSize: 10, fill: "#9ca3af" }} />
                <Tooltip contentStyle={{ fontSize: 11, borderRadius: 8 }} />
                <Bar dataKey="actual" radius={[3, 3, 0, 0]} name="Actual" barSize={totalSprintCount > 8 ? 14 : 20}>
                  {plannedVsActual.map((entry, index) => {
                    const isSelected = sortedSprints[index] && sid(sortedSprints[index].id) === sid(sprint?.id);
                    return <Cell key={index} fill={isSelected ? "#1e40af" : "#2563eb"} />;
                  })}
                </Bar>
                <Bar dataKey="planned" radius={[3, 3, 0, 0]} name="Planned" barSize={totalSprintCount > 8 ? 14 : 20}>
                  {plannedVsActual.map((entry, index) => {
                    const isSelected = sortedSprints[index] && sid(sortedSprints[index].id) === sid(sprint?.id);
                    return <Cell key={index} fill={isSelected ? "#93c5fd" : "#bfdbfe"} />;
                  })}
                </Bar>
                {(() => {
                  const idx = sortedSprints.findIndex((s) => sid(s.id) === sid(sprint?.id));
                  if (idx === -1 || !plannedVsActual[idx]) return null;
                  return (
                    <ReferenceLine
                      x={plannedVsActual[idx].name}
                      stroke="#2563eb"
                      strokeOpacity={0.12}
                      strokeWidth={PX_PER_SPRINT - 10}
                      label={false}
                    />
                  );
                })()}
              </BarChart>
            </div>
          </div>
          {/* Scroll hint when there are more sprints than fit in the card */}
          {totalSprintCount > 6 && (
            <div style={{ fontSize: 10, color: "#9ca3af", textAlign: "center", marginTop: 2 }}>
              ← scroll to see all {totalSprintCount} sprints →
            </div>
          )}
        </ChartCard>
      </Col>

      {/* ── Velocity Trend — ALL sprints with scroll ── */}
      <Col md={4}>
        <ChartCard
          title="Velocity Trend"
          subtitle={`Story points per sprint · ${totalSprintCount} sprints`}
        >
          {/* Same pattern: scrollable container, inner div sized to all sprints */}
          <div ref={velRef} style={{ overflowX: "auto", overflowY: "hidden" }}>
            <div style={{ width: velocityInnerWidth, minWidth: "100%" }}>
              <LineChart
                width={velocityInnerWidth}
                height={totalSprintCount > 8 ? 195 : 175}
                data={velocityData}
                margin={{ top: 4, right: 8, left: -22, bottom: totalSprintCount > 8 ? 55 : 4 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                <XAxis
                  dataKey="name"
                  tick={{ fontSize: 10, fill: "#9ca3af" }}
                  interval={0}          // ← every sprint labeled
                  angle={totalSprintCount > 8 ? -35 : 0}
                  textAnchor={totalSprintCount > 8 ? "end" : "middle"}
                  height={totalSprintCount > 8 ? 50 : 20}
                />
                <YAxis tick={{ fontSize: 10, fill: "#9ca3af" }} />
                <Tooltip contentStyle={{ fontSize: 11, borderRadius: 8 }} />
                <Line
                  type="monotone"
                  dataKey="velocity"
                  stroke="#2563eb"
                  strokeWidth={2}
                  dot={(props) => {
                    const { cx, cy, index } = props;
                    const isSelected = sortedSprints[index] && sid(sortedSprints[index].id) === sid(sprint?.id);
                    return (
                      <circle
                        key={index}
                        cx={cx}
                        cy={cy}
                        r={isSelected ? 7 : 3}
                        fill={isSelected ? "#2563eb" : "#fff"}
                        stroke="#2563eb"
                        strokeWidth={isSelected ? 3 : 2}
                        style={isSelected ? { filter: "drop-shadow(0 0 4px #2563eb88)" } : {}}
                      />
                    );
                  }}
                  activeDot={{ r: 5 }}
                />
                {(() => {
                  const idx = sortedSprints.findIndex((s) => sid(s.id) === sid(sprint?.id));
                  if (idx === -1 || !velocityData[idx]) return null;
                  return (
                    <ReferenceLine
                      x={velocityData[idx].name}
                      stroke="#2563eb"
                      strokeOpacity={0.12}
                      strokeWidth={PX_PER_VEL - 8}
                      label={false}
                    />
                  );
                })()}
              </LineChart>
            </div>
          </div>
          {totalSprintCount > 6 && (
            <div style={{ fontSize: 10, color: "#9ca3af", textAlign: "center", marginTop: 2 }}>
              ← scroll to see all {totalSprintCount} sprints →
            </div>
          )}
        </ChartCard>
      </Col>

    </Row>
  );
};

export default Charts;
